import {
  AspectRatios,
  Broll,
  BrollEffects,
  BrollType,
  CurrentEffect,
  VideoMetadata,
} from '../types';
import { makeTransform, scale } from '@remotion/animation-utils';
import { interpolate, staticFile } from 'remotion';
import { BROLL_EFFECTS } from './constant';
import {UPLOADED_PUBLIC_ASSETS_URL_2} from '../utils/constant';

const getZoomEffect = (effect: string, frame: number, fps: number, start: number) => {
  // Default zoom effect: zoom-in
  let zoomEffect = scale(
    interpolate(frame, [start, start + fps / 2], [1, 1], {
      extrapolateLeft: 'clamp',
      extrapolateRight: 'clamp',
    })
  );

  // Define effects based on the broll.effect key
  switch (effect) {
    case BrollEffects.ZoomIn:
      zoomEffect = scale(
        interpolate(frame, [start, start + fps / 5], [1, 1.35], {
          extrapolateLeft: 'clamp',
          extrapolateRight: 'clamp',
        })
      );
      break;
    case BrollEffects.ZoomOut:
      zoomEffect = scale(
        interpolate(frame, [start, start + fps / 5], [1.35, 1], {
          extrapolateLeft: 'clamp',
          extrapolateRight: 'clamp',
        })
      );
      break;
    case BrollEffects.ZoomSlow:
      zoomEffect = scale(
        interpolate(frame, [start, start + fps * 2], [1, 1.15], {
          extrapolateLeft: 'clamp',
          extrapolateRight: 'clamp',
        })
      );
      break;
    case BrollEffects.ZoomFast:
      zoomEffect = scale(
        interpolate(frame, [start, start + fps / 3], [1, 1.35], {
          extrapolateLeft: 'clamp',
          extrapolateRight: 'clamp',
        })
      );
      break;
    case BrollEffects.NoAnimation:
      zoomEffect = scale(
        interpolate(frame, [start, start + 1], [1, 1], {
          extrapolateLeft: 'clamp',
          extrapolateRight: 'clamp',
        })
      );
      break;
    default:
      break;
  }

  return zoomEffect;
};

const getBrollEffect = (broll: Broll, key: string) => {
  const effectName = (broll as any)[key];
  const effect = BROLL_EFFECTS.find((eff) => eff.id === effectName);
  return effect || BROLL_EFFECTS[0];
};

export const getCurrentEffect = (
  brolls: Broll[],
  videoMetadata: VideoMetadata,
  frame: number,
  key: string,
  isRender = false,
  cdnBaseUrlPath = ''
): CurrentEffect => {
  const activeBroll = brolls.find((broll, index) => {
    const start = broll.startTime * videoMetadata.fps;
    const duration = (broll.endTime - broll.startTime) * videoMetadata.fps;
    // Check if the current frame falls within this b-roll's time range
    return frame >= start && frame < start + duration;
  });

  const selectedEffect = activeBroll ? getBrollEffect(activeBroll, key) : BROLL_EFFECTS[0];

  if (activeBroll && (activeBroll as any)[key] === BrollEffects.NoAnimation) {
    return {
      activeBroll,
      selectedEffect,
      transform: 'none',
      isVideo: false,
      videoPath: '',
    };
  }

  // If there's an active b-roll, apply the transition effect
  if (activeBroll && selectedEffect?.type === 'style') {
    const transitionEffect = getZoomEffect(
      (activeBroll as any)[key] as string,
      frame,
      videoMetadata.fps,
      activeBroll.startTime * videoMetadata.fps
    );
    return {
      activeBroll,
      selectedEffect,
      transform: makeTransform([transitionEffect]),
      isVideo: false,
      videoPath: '',
    };
  } else if (selectedEffect?.type === 'video') {
    return {
      activeBroll,
      selectedEffect,
      transform: 'none',
      isVideo: true,
      videoPath:
        selectedEffect.type === 'video'
          ? isRender
            ? `${UPLOADED_PUBLIC_ASSETS_URL_2}videos/${selectedEffect.id}.mp4`
            : `${UPLOADED_PUBLIC_ASSETS_URL_2}videos/${selectedEffect.id}.mp4`
          : '',
    };
  }

  // Return "none" if no active b-roll found
  return {
    activeBroll,
    selectedEffect,
    transform: 'none',
    isVideo: false,
    videoPath: '',
  };
};
const makeEven = (value: number): number => (value % 2 === 0 ? value : value + 1);

export const calculatePlayerDimensions = ({
  videoMetadata,
  aspectRatio,
}: {
  videoMetadata: VideoMetadata;
  aspectRatio: AspectRatios;
}) => {
  if (!videoMetadata) return { width: 0, height: 0 };

  const originalWidth = videoMetadata.width;
  const originalHeight = videoMetadata.height;

  if (aspectRatio === AspectRatios.ORIGINAL) {
    return {
      width: makeEven(originalWidth),
      height: makeEven(originalHeight),
    };
  }

  const [ratioWidth, ratioHeight] = aspectRatio.split(':').map(Number);

  let width, height;

  if (originalWidth / ratioWidth > originalHeight / ratioHeight) {
    height = originalHeight;
    width = Math.round((originalHeight / ratioHeight) * ratioWidth);
  } else {
    width = originalWidth;
    height = Math.round((originalWidth / ratioWidth) * ratioHeight);
  }

  return {
    width: makeEven(width),
    height: makeEven(height),
  };
};

export const calculateColumnGap = (width: number): number => {
  const factor = 0.01015625; // Ratio based on (20 - 8) / (1920 - 640)

  const baseGap = 8; // Column gap at 640px
  const baseWidth = 640;

  const columnGap = baseGap + (width - baseWidth) * factor;

  return columnGap;
};
